import axios from "axios";

function normalizeBackendOrigin(raw) {
  if (raw == null || raw === "") return "";
  let s = String(raw).trim().replace(/\/+$/, "");
  // Avoid …/api/api/players (404) when env mistakenly includes /api
  if (/\/api$/i.test(s)) {
    s = s.replace(/\/api$/i, "").replace(/\/+$/, "");
  }
  return s;
}

const BACKEND_URL = normalizeBackendOrigin(process.env.REACT_APP_BACKEND_URL);
/** Absolute backend (`https://api.example.com/api`) or same-origin `/api` when env is unset (needs Render rewrite). */
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

if (process.env.NODE_ENV === "production" && !BACKEND_URL) {
  console.info(
    "[Trystomatic] API base is same-origin /api. On Render Static Site add a Rewrite (FIRST): source /api/* → https://YOUR-BACKEND.onrender.com/api/* ; then /* → /index.html for React Router.",
  );
}

/** Cold Render wakes + busy tiers can exceed default patience; retry handles transient 502/429. */
const REQUEST_TIMEOUT_MS = 120_000;

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

const STORAGE_KEY = "trystomatic.user";
const ADMIN_TOKEN_KEY = "trystomatic.admin.token";

export function getUser() {
  try {
    let raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(STORAGE_KEY);
      if (legacy) {
        sessionStorage.setItem(STORAGE_KEY, legacy);
        localStorage.removeItem(STORAGE_KEY);
        raw = legacy;
      }
    }
    const user = raw ? JSON.parse(raw) : null;
    // Signed sessions only — drop legacy logins saved before JWT auth
    if (user && !user.is_guest && !user.token) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return user;
  } catch { return null; }
}

export function setUser(u) {
  if (u) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  else sessionStorage.removeItem(STORAGE_KEY);
}

export function getAdminToken() {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setAdminToken(token) {
  if (token) sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function clearAdminSession() {
  setAdminToken(null);
}

export const api = axios.create({ baseURL: API, timeout: REQUEST_TIMEOUT_MS });

api.interceptors.request.use((cfg) => {
  const u = getUser();
  if (u?.token) {
    cfg.headers.Authorization = `Bearer ${u.token}`;
  }
  return cfg;
});

const adminApi = axios.create({ baseURL: API, timeout: REQUEST_TIMEOUT_MS });
adminApi.interceptors.request.use((cfg) => {
  const token = getAdminToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export async function loginSleeper(username) {
  const { data } = await api.post(`/auth/sleeper`, { username });
  setUser(data);
  return data;
}

export async function loginAdmin(password) {
  const { data } = await adminApi.post(`/admin/login`, { password });
  setAdminToken(data.token);
  return data;
}

export async function fetchPlayers(format) {
  let lastErr;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await api.get(`/players`, { params: { format } });
      return data;
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const code = err?.code;
      const transient =
        code === "ECONNABORTED" ||
        status === 429 ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (!status && err?.message === "Network Error");
      if (!transient || attempt === 1) throw err;
      await sleep(2000);
    }
  }
  throw lastErr;
}

export async function fetchPublicTrystomaticTrack() {
  const { data } = await api.get(`/public/trystomatic-leagues`);
  return data;
}

export async function fetchPublicSleeperLookup(username, { signal } = {}) {
  const u = encodeURIComponent(String(username || "").trim());
  let lastErr;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await api.get(`/public/sleeper-lookup/${u}`, { signal });
      return data;
    } catch (err) {
      lastErr = err;
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") throw err;
      const status = err?.response?.status;
      const code = err?.code;
      const transient =
        code === "ECONNABORTED" ||
        status === 429 ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (!status && err?.message === "Network Error");
      if (!transient || attempt === 1) throw err;
      await sleep(2000);
    }
  }
  throw lastErr;
}

export async function fetchAdminRankings(format = "superflex") {
  const { data } = await api.get(`/admin-rankings`, { params: { format } });
  return data;
}

export async function saveAdminRankingSettings(payload) {
  const { data } = await adminApi.put(`/admin-rankings`, payload);
  return data;
}

export async function fetchFpRankings(format) {
  const { data } = await adminApi.get(`/fp-rankings`, { params: { format } });
  return data;
}

export async function uploadFpCsv(format, csv) {
  const { data } = await adminApi.post(`/fp-rankings/csv-body`, { format, csv });
  return data;
}

export async function clearFp(format) {
  const { data } = await adminApi.delete(`/fp-rankings`, { params: { format } });
  return data;
}

export async function refreshData() {
  const { data } = await adminApi.post(`/refresh`);
  return data;
}

export async function fetchLeagues() {
  const { data } = await api.get(`/sleeper/leagues`);
  return data;
}

export async function fetchLeague(leagueId) {
  const { data } = await api.get(`/sleeper/league/${leagueId}`);
  return data;
}

export async function fetchLeagueTrades(leagueId) {
  const { data } = await api.get(`/sleeper/league/${leagueId}/trades`);
  return data;
}

export async function fetchMySleeperNews({ leagueId, limit = 40 } = {}) {
  const params = { limit };
  if (leagueId) params.league_id = leagueId;
  const { data } = await api.get(`/sleeper/news`, { params });
  return data;
}

export async function fetchPublicSleeperNews(username, { leagueId, limit = 40, signal } = {}) {
  const u = encodeURIComponent(String(username || "").trim());
  const params = { limit };
  if (leagueId) params.league_id = leagueId;
  const { data } = await api.get(`/public/sleeper-news/${u}`, { params, signal });
  return data;
}
